import React, { useState } from 'react';
import {
    LayoutDashboard,
    History,
    UploadCloud,
    LogOut,
    User,
    AlertTriangle,
    X,
    MonitorPlay,
    BarChart3,
    Building2,
} from 'lucide-react';
import Logo from './Logo';
import './Sidebar.css';

const Sidebar = ({ activeTab, setActiveTab, onLogout, isOpen = false, onClose, isSuperAdmin = false }) => {
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    // On mobile, tapping a menu item should both switch tab and close the drawer.
    const handleSelect = (id) => {
        setActiveTab(id);
        if (onClose) onClose();
    };

    const handleLogoutClick = () => {
        setShowLogoutConfirm(true);
    };

    const handleConfirmLogout = () => {
        setShowLogoutConfirm(false);
        onLogout();
    };

    const handleCancelLogout = () => {
        setShowLogoutConfirm(false);
    };
    // Note: "Playlists" (content packages / reusable bundles) is intentionally
    // NOT a top-level sidebar item any more. Auto-merge of overlapping schedules
    // (Phase 7) + quick-schedule-from-library (Phase 9) cover the common cases,
    // so playlists is a power-user feature moved under Scheduler → Packages.
    // Screensaver is no longer a top-level nav item — the platform ships a
    // default screensaver and per-screen overrides live in the Screens tab,
    // so a dedicated menu entry was adding navigation weight without
    // matching a real workflow.
    const menuItems = [
        { id: 'history', label: 'Calendar', icon: LayoutDashboard },
        { id: 'tvList', label: 'Screens', icon: MonitorPlay },
        { id: 'websiteUpload', label: 'Media', icon: UploadCloud },
        { id: 'analytics', label: 'Analytics', icon: BarChart3 },
        // Multi-city management — super-admins only.
        ...(isSuperAdmin ? [{ id: 'cities', label: 'Cities', icon: Building2 }] : []),
    ];

    return (
        <>
            <div
                className={`sidebar-backdrop${isOpen ? ' sidebar-backdrop--open' : ''}`}
                onClick={onClose}
                aria-hidden="true"
            />
            <div
                className={`sidebar${isOpen ? ' sidebar--open' : ''}`}
                style={{
                    width: '280px',
                    height: '100vh',
                    backgroundColor: 'var(--sidebar-bg)',
                    borderRight: '1px solid var(--border-color)',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    zIndex: 10,
                    flexShrink: 0,
                }}
            >
            {/* Logo Area */}
            <div style={{ padding: '24px 20px', borderBottom: '1px solid var(--border-color)' }}>
                <Logo />
            </div>

            {/* Navigation */}
            <nav style={{ flex: 1, padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {menuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.id;

                    return (
                        <button
                            key={item.id}
                            onClick={() => handleSelect(item.id)}
                            className="sidebar__menu-item"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px 16px',
                                width: '100%',
                                backgroundColor: isActive ? 'var(--primary-light)' : 'transparent',
                                color: isActive ? 'var(--primary-color)' : 'var(--text-secondary)',
                                border: isActive ? '1px solid var(--primary-color)' : '1px solid transparent',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                textAlign: 'left',
                                transition: 'all 0.2s ease',
                                fontWeight: isActive ? '600' : '500',
                                outline: 'none',
                            }}
                        >
                            <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                            <span style={{ fontSize: '15px' }}>{item.label}</span>
                        </button>
                    );
                })}
            </nav>

            {/* Footer / User Profile */}
            <div style={{ padding: '20px', borderTop: '1px solid var(--border-color)' }}>
                <button
                    onClick={handleLogoutClick}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        borderRadius: '12px',
                        backgroundColor: '#f9fafb',
                        cursor: 'pointer',
                        border: 'none',
                        width: '100%',
                        transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                    }}
                >
                    <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        backgroundColor: '#e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6b7280'
                    }}>
                        <User size={20} />
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden', textAlign: 'left' }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                            {localStorage.getItem('userEmail') || 'Admin User'}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Click to logout</div>
                    </div>
                    <LogOut size={16} color="var(--text-secondary)" />
                </button>
            </div>

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }}
                    onClick={handleCancelLogout}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            padding: '24px',
                            maxWidth: '400px',
                            width: '90%',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: '#fef3c7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <AlertTriangle size={20} color="#f59e0b" />
                            </div>
                            <h3 style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: '#111827',
                                margin: 0,
                                flex: 1,
                            }}>
                                Confirm Logout
                            </h3>
                            <button
                                onClick={handleCancelLogout}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '4px',
                                    color: '#6b7280',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <p style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            marginBottom: '24px',
                            lineHeight: '1.5',
                        }}>
                            Are you sure you want to logout? You will need to sign in again to access the dashboard.
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end',
                        }}>
                            <button
                                onClick={handleCancelLogout}
                                style={{
                                    padding: '10px 20px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    backgroundColor: '#f3f4f6',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#e5e7eb';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmLogout}
                                style={{
                                    padding: '10px 20px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#ffffff',
                                    backgroundColor: '#c8a147',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#b8913f';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#c8a147';
                                }}
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </>
    );
};

export default Sidebar;
